import { useContext, useEffect, useState } from "react";
import { Card, Row, Col, Avatar, Typography, Descriptions, Tag, Divider, Table, Button, Upload, Modal, Form, Input, Select, DatePicker, message, InputNumber, Space } from "antd";
import {
    ArrowLeftOutlined, EditOutlined, CameraOutlined, DownloadOutlined, SaveOutlined, CloseOutlined, PlusOutlined, DeleteOutlined, CalendarOutlined,
    LogoutOutlined,
    SafetyCertificateOutlined
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { AuthContext } from "../context/AuthContext";
import ImgCrop from 'antd-img-crop';
import LeaveCalendar from "../components/employee/LeaveCalendar";
import ViewOneEmployeeRecord from "../components/hr/ViewOneEmployeeRecord";
import ViewOneEmpPerm from "../components/ViewOneEmpPerm";


const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    return d.toLocaleDateString('en-GB');
};

export default function EmployeeProfile({ onBack, empId, DisableEditOption }) {
    const { authData, backUrl } = useContext(AuthContext)

    const [cropModalVisible, setCropModalVisible] = useState(false);
    const [tempImageUrl, setTempImageUrl] = useState(null);
    const [rotation, setRotation] = useState(0);
    const [zoom, setZoom] = useState(1);

    const [showCalendar, setShowCalendar] = useState(false);
    const [showLeaves, setShowLeaves] = useState(false);
    const [showPermissions, setShowPermissions] = useState(false);

    const [employee, setEmployee] = useState({});
    const [uploading, setUploading] = useState(false);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editSection, setEditSection] = useState('');
    const [form] = Form.useForm();

    const idToUse = empId || authData?.employee?.id;

    const closeCalendar = () => setShowCalendar(false);
    const closeLeaves = () => setShowLeaves(false);
    const closePermissions = () => setShowPermissions(false);


    const getEmployeeData = async () => {
        const res = await axios.get(`${backUrl}/api/emp/employees/${idToUse}/`, {
            headers: {
                Authorization: `Bearer ${authData?.access}`
            }
        })

        console.log("Employee Data : ", res.data)

        setEmployee(res.data)
    }

    useEffect(() => {
        getEmployeeData()
    }, [])

    useEffect(() => {
        if (employee?.photo) {
            setPhotoUrl(employee.photo);
        }
    }, [employee?.photo])

    // Helper function to parse JSON strings
    const parseArrayField = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        if (typeof field === 'string') {
            try {
                const parsed = JSON.parse(field);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                console.error('Failed to parse field:', field, e);
                return [];
            }
        }
        return [];
    };

    // Parse array fields
    const educationHistory = parseArrayField(employee.education_history);
    const careerHistory = parseArrayField(employee.career_history);
    const referenceHistory = parseArrayField(employee.reference_history);
    const siblingDetails = parseArrayField(employee.sibling_details);
    const childDetails = parseArrayField(employee.child_details);

    if (!employee) return <p>No employee data found.</p>;

    const handlePhotoSelect = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            setTempImageUrl(e.target.result);
            setCropModalVisible(true);
        };
        reader.readAsDataURL(file);
        return false; // Prevent auto upload
    };

    const handlePhotoUpload = async (file) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('employee_id', employee.id);

        try {
            const response = await axios.put(
                `${backUrl}/api/emp/employees/${employee.id}/`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );

            if (response.status === 200 || response.status === 201) {
                const localUrl = URL.createObjectURL(file);
                setPhotoUrl(localUrl);
                setEmployee(prev => ({ ...prev, photo: localUrl }));
                message.success('Photo updated successfully!');
                setCropModalVisible(false);
                setRotation(0);
                setZoom(1);
            } else {
                message.error('Failed to upload photo');
            }
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Error uploading photo');
        } finally {
            setUploading(false);
        }
    };

    // Rotate image helper
    const rotateImage = (direction) => {
        setRotation(prev => {
            const newRotation = direction === 'left' ? prev - 90 : prev + 90;
            return newRotation % 360;
        });
    };

    const openEditModal = (section, initialValues) => {
        setEditSection(section);
        form.setFieldsValue(initialValues);
        setEditModalVisible(true);
    };

    const handleEditSubmit = async () => {
        try {
            const values = await form.validateFields();
            setUploading(true);

            const formData = new FormData();
            formData.append('employee_id', employee.id);

            // Add all form values to FormData
            Object.keys(values).forEach(key => {
                if (values[key] !== null && values[key] !== undefined) {
                    // Handle date fields
                    if (values[key] instanceof dayjs || (values[key] && values[key].$d)) {
                        formData.append(key, dayjs(values[key]).format('YYYY-MM-DD'));
                    }
                    // Handle arrays
                    else if (Array.isArray(values[key])) {
                        formData.append(key, JSON.stringify(values[key]));
                    }
                    // Handle objects
                    else if (typeof values[key] === 'object') {
                        formData.append(key, JSON.stringify(values[key]));
                    }
                    // Handle regular values
                    else {
                        formData.append(key, values[key]);
                    }
                }
            });

            const response = await axios.put(
                `${backUrl}/api/emp/employees/${employee.id}/`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );

            if (response.status === 200 || response.status === 201) {
                message.success('Details updated successfully!');
                setEmployee({ ...employee, ...values });
                setEditModalVisible(false);
                form.resetFields();
            } else {
                message.error('Failed to update details');
            }
        } catch (error) {
            console.error('Update error:', error);
            message.error('Error updating details');
        } finally {
            setUploading(false);
        }
    };

    const renderEditButton = (section, initialValues) => (
        // Only show edit button if DisableEditOption is not true AND empId is not null
        !DisableEditOption ? (
            <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => openEditModal(section, initialValues)}
                style={{ float: 'right' }}
            >
                Edit
            </Button>
        ) : null
    );

    const renderArrayTable = (data, columns) => {
        // Ensure data is always an array
        const arrayData = Array.isArray(data) ? data : (data ? [data] : []);

        if (arrayData.length === 0) {
            return <p style={{ color: '#999' }}>No records found</p>;
        }
        return <Table components={{
            header: {
                cell: (props) => (
                    <th {...props} style={{ backgroundColor: "#d0e5f8e4", color: '#0e497b', marginTop: '20px' }}>
                        {props.children}
                    </th>
                ),
            },
        }} dataSource={arrayData} columns={columns} pagination={false} rowKey={(record, index) => index} size="small" bordered />;
    };

    const renderDocumentLink = (docPath, docName) => {
        if (!docPath) return "-";
        return (
            <Button
                size="small"
                icon={<DownloadOutlined />}
                href={docPath}
                target="_blank"
                type="link"
            >
                View {docName}
            </Button>
        );
    };

    const roundValue = (value) => {
        if (value === null || value === undefined) return "-";
        return Math.round(Number(value));
    };

    const getEditFormFields = (section) => {
        switch (section) {
            case 'personal':
                return (
                    <>
                        <Form.Item label="Salutation" name="salutation">
                            <Select>
                                <Option value="Mr.">Mr.</Option>
                                <Option value="Ms.">Ms.</Option>
                                <Option value="Mrs.">Mrs.</Option>
                                <Option value="Dr.">Dr.</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="First Name" name="first_name" >
                            <Input />
                        </Form.Item>
                        <Form.Item label="Last Name" name="last_name" >
                            <Input />
                        </Form.Item>
                        <Form.Item label="Surname" name="surname">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Initial" name="initial">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Gender" name="gender">
                            <Select>
                                <Option value="Male">Male</Option>
                                <Option value="Female">Female</Option>
                                <Option value="Other">Other</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="Date of Birth" name="date_of_birth">
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item label="Blood Group" name="blood_group">
                            <Select>
                                <Option value="A+">A+</Option>
                                <Option value="A-">A-</Option>
                                <Option value="B+">B+</Option>
                                <Option value="B-">B-</Option>
                                <Option value="O+">O+</Option>
                                <Option value="O-">O-</Option>
                                <Option value="AB+">AB+</Option>
                                <Option value="AB-">AB-</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="Mother Tongue" name="mother_tongue">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Religion" name="religion">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Caste" name="caste">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Nationality" name="country">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Place of Birth" name="place_of_birth">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Height (cm)" name="height">
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item label="Weight (kg)" name="weight">
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item label="Marital Status" name="marital_status">
                            <Select>
                                <Option value="Single">Single</Option>
                                <Option value="Married">Married</Option>
                                <Option value="Divorced">Divorced</Option>
                                <Option value="Widowed">Widowed</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="Wedding Date" name="wedding_date">
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </>
                );
            case 'family':
                return (
                    <>
                        <Form.Item label="Father Name" name="father_name">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Father Profession" name="father_profession">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Mother Name" name="mother_name">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Mother Profession" name="mother_profession">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Spouse Name" name="spouse_name">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Spouse Date of Birth" name="spouse_dob">
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </>
                );
            case 'contact':
                return (
                    <>
                        {/* <Form.Item label="Email" name="official_email_id" rules={[{ type: 'email' }]}>
                            <Input />
                        </Form.Item> */}
                        <Form.Item label="Mobile Number" name="mobile_number">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Landline Number" name="landline_extension">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Emergency Contact Person" name="emergency_contact_person">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Emergency Contact Number" name="emergency_contact_number">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Personal Email Id" name="email_id">
                            <Input />
                        </Form.Item>
                        {/* <Form.Item label="Official CUG No" name="official_cug_number">
                            <Input />
                        </Form.Item> */}
                    </>
                );
            case 'job':
                return (
                    <>
                        <Form.Item label="Employee Code" name="emp_code">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Location" name="location">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Date of Joining" name="date_of_joining">
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                        {/* <Form.Item label="Probation Period (Months)" name="probation_period_months">
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item> */}
                    </>
                );
            case 'statutory':
                return (
                    <>
                        <Form.Item label="Aadhaar Number" name="aadhaar_card_number">
                            <Input maxLength={12} />
                        </Form.Item>
                        <Form.Item label="PAN Number" name="pan_card_number">
                            <Input maxLength={10} />
                        </Form.Item>
                        <Form.Item label="ESIC IP Number" name="esic_ip_number">
                            <Input />
                        </Form.Item>
                        <Form.Item label="UAN Number" name="uan_number">
                            <Input />
                        </Form.Item>
                    </>
                );
            case 'bank':
                return (
                    <>
                        <Form.Item label="Bank Name" name="bank_name">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Bank Branch" name="branch_name">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Account Number" name="bank_account_number">
                            <Input />
                        </Form.Item>
                        <Form.Item label="IFSC Code" name="ifsc_code">
                            <Input />
                        </Form.Item>
                        <Form.Item label="CUB Account Number" name="cub_account_number">
                            <Input />
                        </Form.Item>
                    </>
                );
            case 'address':
                return (
                    <>
                        <Divider>Current Address</Divider>
                        <Form.Item label="Address" name="address_for_communication">
                            <TextArea rows={2} />
                        </Form.Item>
                        <Form.Item label="Area" name="area">
                            <Input />
                        </Form.Item>
                        <Form.Item label="City" name="city">
                            <Input />
                        </Form.Item>
                        <Form.Item label="State" name="state">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Country" name="country">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Pincode" name="pincode">
                            <Input />
                        </Form.Item>
                        <Divider>Permanent Address</Divider>
                        <Form.Item label="Address" name="permanent_address_for_communication">
                            <TextArea rows={2} />
                        </Form.Item>
                        <Form.Item label="Area" name="permanent_area">
                            <Input />
                        </Form.Item>
                        <Form.Item label="City" name="permanent_city">
                            <Input />
                        </Form.Item>
                        <Form.Item label="State" name="permanent_state">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Country" name="permanent_country">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Pincode" name="permanent_pincode">
                            <Input />
                        </Form.Item>
                    </>
                );
            case 'skills':
                return (
                    <>
                        {/* <Form.Item label="Skills" name="skills">
                            <TextArea rows={3} placeholder="e.g., JavaScript, React, Python" />
                        </Form.Item>
                        <Form.Item label="Certifications" name="certifications">
                            <TextArea rows={3} placeholder="e.g., AWS Certified, PMP" />
                        </Form.Item> */}
                        <Form.Item label="Languages Known" name="languages_known">
                            <Input placeholder="e.g., English, Tamil, Hindi" />
                        </Form.Item>
                    </>
                );
            case 'education':
                return (
                    <Form.List name="education_history">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Card key={key} style={{ marginBottom: 16, background: '#ffffffff' }}>
                                        <Space style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Title level={5}>Education Entry {name + 1}</Title>
                                            <Button
                                                type="link"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(name)}
                                            >
                                                Remove
                                            </Button>
                                        </Space>
                                        <Form.Item
                                            {...restField}
                                            label="Educational Qualification"
                                            name={[name, 'educational_qualification']}
                                        >
                                            <Input placeholder="e.g., Bachelor of Engineering" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="Institution Name"
                                            name={[name, 'educational_institution_name']}
                                        >
                                            <Input placeholder="e.g., Anna University" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="Degree/Diploma"
                                            name={[name, 'degree_or_diploma']}
                                        >
                                            <Input placeholder="e.g., B.E Computer Science" />
                                        </Form.Item>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item
                                                    {...restField}
                                                    label="From Date"
                                                    name={[name, 'course_duration_from']}
                                                >
                                                    <DatePicker style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item
                                                    {...restField}
                                                    label="To Date"
                                                    name={[name, 'course_duration_to']}
                                                >
                                                    <DatePicker style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        {/* <Form.Item
                                            {...restField}
                                            label="Extra Curricular Activities"
                                            name={[name, 'extra_curricular_activity']}
                                        >
                                            <TextArea rows={2} />
                                        </Form.Item> */}
                                    </Card>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Add Education Entry
                                </Button>
                            </>
                        )}
                    </Form.List>
                );
            case 'career':
                return (
                    <Form.List name="career_history">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Card key={key} style={{ marginBottom: 16, background: '#ffffffff' }}>
                                        <Space style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Title level={5}>Career Entry {name + 1}</Title>
                                            <Button
                                                type="link"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(name)}
                                            >
                                                Remove
                                            </Button>
                                        </Space>
                                        <Form.Item
                                            {...restField}
                                            label="Organisation"
                                            name={[name, 'organisation']}
                                        >
                                            <Input placeholder="Company Name" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="Designation"
                                            name={[name, 'designation']}
                                        >
                                            <Input placeholder="Job Title" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="Key Responsibilities"
                                            name={[name, 'keyresponsibilities']}
                                        >
                                            <TextArea rows={3} placeholder="Describe your responsibilities" />
                                        </Form.Item>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item
                                                    {...restField}
                                                    label="From Date"
                                                    name={[name, 'work_duration_from']}
                                                >
                                                    <DatePicker style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item
                                                    {...restField}
                                                    label="To Date"
                                                    name={[name, 'work_duration_to']}
                                                >
                                                    <DatePicker style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Add Career Entry
                                </Button>
                            </>
                        )}
                    </Form.List>
                );
            case 'reference':
                return (
                    <Form.List name="reference_history">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Card key={key} style={{ marginBottom: 16, background: '#ffffffff' }}>
                                        <Space style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Title level={5}>Reference {name + 1}</Title>
                                            <Button
                                                type="link"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(name)}
                                            >
                                                Remove
                                            </Button>
                                        </Space>
                                        <Form.Item
                                            {...restField}
                                            label="Reference Name"
                                            name={[name, 'referal_name']}
                                        >
                                            <Input placeholder="Full Name" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="Designation"
                                            name={[name, 'referal_designation']}
                                        >
                                            <Input placeholder="Job Title" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="Email"
                                            name={[name, 'referal_email_id']}
                                        >
                                            <Input placeholder="email@example.com" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="Mobile Number"
                                            name={[name, 'referal_mobile_number']}
                                        >
                                            <Input placeholder="Phone Number" />
                                        </Form.Item>
                                    </Card>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Add Reference
                                </Button>
                            </>
                        )}
                    </Form.List>
                );
            case 'siblings':
                return (
                    <Form.List name="sibling_details">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Card key={key} style={{ marginBottom: 16, background: '#ffffffff' }}>
                                        <Space style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Button
                                                type="link"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(name)}
                                            >
                                                Remove
                                            </Button>
                                        </Space>
                                        <Form.Item
                                            {...restField}
                                            label="Name"
                                            name={[name, 'sibling_name']}
                                        >
                                            <Input placeholder="Sibling's Name" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="DOB"
                                            name={[name, 'sibling_dob']}
                                        >
                                            <input type="date" style={{ width: '100%' }} />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="Gender"
                                            name={[name, 'sibling_gender']}
                                        >
                                            <Input placeholder="Gender" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="Profession"
                                            name={[name, 'sibling_profession']}
                                        >
                                            <Input placeholder="Profession" />
                                        </Form.Item>
                                    </Card>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Add Sibling
                                </Button>
                            </>
                        )}
                    </Form.List>
                );
            case 'children':
                return (
                    <Form.List name="child_details">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Card key={key} style={{ marginBottom: 16, background: '#ffffffff' }}>
                                        <Space style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Title level={5}>Child {name + 1}</Title>
                                            <Button
                                                type="link"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(name)}
                                            >
                                                Remove
                                            </Button>
                                        </Space>
                                        <Form.Item
                                            {...restField}
                                            label="Name"
                                            name={[name, 'child_name']}
                                        >
                                            <Input placeholder="Child's Name" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="Date of Birth"
                                            name={[name, 'child_dob']}
                                        >
                                            <input type="date" style={{ width: '100%' }} />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            label="Gender"
                                            name={[name, 'child_gender']}
                                        >
                                            <Input placeholder="Child's Gender" />
                                        </Form.Item>
                                    </Card>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Add Child
                                </Button>
                            </>
                        )}
                    </Form.List>
                );
            default:
                return null;
        }
    };

    return (
        <div style={{ padding: "24px", background: "#ffffffff", minHeight: "100vh" }}>
            <Button type="link" icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: 16 }}>
                Back
            </Button>

            {/* TOP PROFILE CARD */}
            <Card style={{ marginBottom: 24, borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                <Row gutter={20} align="middle">

                    {/* Photo */}
                    <Col xs={24} md={4} style={{ textAlign: "center" }}>
                        <div style={{ position: "relative", display: "inline-block" }}>
                            <Avatar
                                size={150}
                                src={photoUrl || employee?.photo}
                                style={{ border: "3px solid #0e487bff" }}
                            >
                                {!photoUrl && !employee?.photo && `${employee.first_name?.[0]}`}
                            </Avatar>

                            <ImgCrop
                                rotate
                                aspect={1}
                                quality={1}
                                modalTitle="Crop & Adjust Photo"
                                modalOk="Upload"
                                beforeCrop={(file) => {
                                    const isImage = file.type.startsWith('image/');
                                    if (!isImage) {
                                        message.error('You can only upload image files!');
                                    }
                                    return isImage;
                                }}
                            >
                                <Upload
                                    beforeUpload={handlePhotoUpload}
                                    showUploadList={false}
                                    accept="image/*"
                                >
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={<EditOutlined />}
                                        size="small"
                                        loading={uploading}
                                        style={{
                                            position: "absolute",
                                            bottom: 5,
                                            right: 5,
                                            background: '#0e487bff'
                                        }}
                                    />
                                </Upload>
                            </ImgCrop>
                        </div>

                        <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
                            Click to edit photo
                        </div>
                    </Col>

                    {/* Employee Details */}
                    <Col xs={24} md={14}>
                        <Title level={3} style={{ marginBottom: 4, color: '#0e487bff' }}>
                            {employee.salutation} {employee.first_name} {employee.last_name} {employee.surname} {employee.initial}
                        </Title>

                        <p><strong>Employee Code:</strong> {employee.emp_code || "-"}</p>
                        <p><strong>Organisation:</strong> {employee.organisation_name || "-"}</p>
                        <p><strong>Department:</strong> {employee.department_name || "-"}</p>
                        <p><strong>Designation:</strong> {employee.designation_name || "-"}</p>
                        <p><strong>Email:</strong> {employee.official_email_id || "-"}</p>
                        <p><strong>CUG Number:</strong> {employee.official_cug_number || "-"}</p>
                        <p><strong>Reporting To:</strong> {employee?.reporting_to_name || "-"}</p>

                        {employee.status === "Resigned" ? (
                            <Tag color="red">Resigned</Tag>
                        ) : (
                            <Tag color="green">Active Employee</Tag>
                        )}

                        {employee.employee_type && <Tag color="blue">{employee.employee_type}</Tag>}
                        {employee.employment_status && <Tag color="purple">{employee.employment_status}</Tag>}

                        {
                            empId !== null && (
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                        marginTop: "10px",
                                        flexWrap: "wrap"
                                    }}
                                >
                                    {/* Show Calendar */}
                                    <Button
                                        type="primary"
                                        icon={<CalendarOutlined />}
                                        onClick={() => setShowCalendar(true)}
                                        style={{ backgroundColor: '#0e497b', color: 'white' }}
                                    >
                                    </Button>

                                    {/* Leaves */}
                                    <Button
                                        type="default"
                                        icon={<LogoutOutlined />}
                                        onClick={() => setShowLeaves(true)}
                                        style={{ backgroundColor: '#0e497b', color: 'white' }}

                                    >
                                        Leaves
                                    </Button>

                                    {/* Permissions */}
                                    <Button
                                        type="dashed"
                                        icon={<SafetyCertificateOutlined />}
                                        onClick={() => setShowPermissions(true)}
                                        style={{ backgroundColor: '#0e497b', color: 'white' }}

                                    >
                                        Permissions
                                    </Button>
                                </div>
                            )
                        }
                    </Col>

                </Row>

            </Card>


            <Modal
                title="Calendar"
                open={showCalendar}          // 👈 AntD v5 uses "open"
                onCancel={closeCalendar}
                footer={null}
                width={600}
            >
                <LeaveCalendar employee={employee} />
            </Modal>

            <Modal
                title="Employee Leaves"
                open={showLeaves}
                onCancel={closeLeaves}
                footer={null}
                width={1200}
            >
                <ViewOneEmployeeRecord
                    employee={{
                        id: employee.id,
                        employee: employee, // Wrap the employee data
                        leaves_taken_breakdown: {
                            sick_leave: employee.taken_sick_leave || 0,
                            casual_leave: employee.taken_casual_leave || 0,
                            earned_leave: employee.taken_earned_leave || 0,
                            lop_leave: employee.taken_lop_leave || 0
                        }
                    }}
                    isProfile={true}
                />
            </Modal>

            <Modal
                title="Employee Permissions"
                open={showPermissions}          // 👈 AntD v5 uses "open"
                onCancel={closePermissions}
                footer={null}
                width={1200}
            >
                <ViewOneEmpPerm employee={{
                    id: employee.id,
                    employee: employee, // Wrap the employee data
                    leaves_taken_breakdown: {
                        sick_leave: employee.taken_sick_leave || 0,
                        casual_leave: employee.taken_casual_leave || 0,
                        earned_leave: employee.taken_earned_leave || 0,
                        lop_leave: employee.taken_lop_leave || 0
                    }
                }}
                    isProfile={true} />
            </Modal>


            {/* PERSONAL DETAILS */}
            <Card
                title="Personal Details"
                extra={empId !== null ? renderEditButton('personal', {
                    salutation: employee.salutation,
                    first_name: employee.first_name,
                    last_name: employee.last_name,
                    surname: employee.surname,
                    initial: employee.initial,
                    gender: employee.gender,
                    date_of_birth: employee.date_of_birth ? dayjs(employee.date_of_birth) : null,
                    blood_group: employee.blood_group,
                    mother_tongue: employee.mother_tongue,
                    religion: employee.religion,
                    caste: employee.caste,
                    nationality: employee.nationality,
                    place_of_birth: employee.place_of_birth,
                    height: employee.height,
                    weight: employee.weight,
                    marital_status: employee.marital_status,
                    wedding_date: employee.wedding_date ? dayjs(employee.wedding_date) : null
                }) : null}
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                <Descriptions column={{ xs: 1, sm: 2 }} bordered
                    labelStyle={{ background: "#d0e5f8e4", color: '#0e487bff', fontWeight: 600 }}
                    contentStyle={{ background: "white" }}>
                    <Descriptions.Item label="Full Name">{employee.initial} {employee.first_name} {employee.last_name} {employee.surname}</Descriptions.Item>
                    {/* <Descriptions.Item label="Surname">{employee.surname || "-"}</Descriptions.Item> */}
                    <Descriptions.Item label="Gender">{employee.gender || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Date of Birth">{formatDate(employee.date_of_birth)}</Descriptions.Item>
                    <Descriptions.Item label="Age">{employee.date_of_birth ? new Date().getFullYear() - new Date(employee.date_of_birth).getFullYear() : "-"}</Descriptions.Item>
                    <Descriptions.Item label="Blood Group">{employee.blood_group || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Mother Tongue">{employee.mother_tongue || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Religion">{employee.religion || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Caste">{employee.caste || "-"}</Descriptions.Item>
                    {/* <Descriptions.Item label="Nationality">{employee.nationality || employee.country || "-"}</Descriptions.Item> */}
                    <Descriptions.Item label="Place of Birth">{employee.place_of_birth || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Height">{employee.height ? `${employee.height}` : "-"}</Descriptions.Item>
                    <Descriptions.Item label="Weight">{employee.weight ? `${employee.weight}` : "-"}</Descriptions.Item>
                    <Descriptions.Item label="Marital Status">{employee.marital_status || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Wedding Date">{formatDate(employee.wedding_date)}</Descriptions.Item>

                </Descriptions>
            </Card>

            {/* FAMILY DETAILS */}
            <Card
                title="Family Details"
                extra={empId !== null ? renderEditButton('family', {
                    father_name: employee.father_name,
                    father_profession: employee.father_profession,
                    mother_name: employee.mother_name,
                    mother_profession: employee.mother_profession,
                    spouse_name: employee.spouse_name,
                    spouse_dob: employee.spouse_dob ? dayjs(employee.spouse_dob) : null
                }) : null}
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                <Descriptions column={{ xs: 1, sm: 2 }} bordered
                    labelStyle={{ background: "#d0e5f8e4", color: '#0e487bff', fontWeight: 600 }}>
                    <Descriptions.Item label="Father Name">{employee.father_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Father Profession">{employee.father_profession || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Mother Name">{employee.mother_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Mother Profession">{employee.mother_profession || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Spouse Name">{employee.spouse_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Spouse Date of Birth">{formatDate(employee.spouse_dob)}</Descriptions.Item>
                </Descriptions>
            </Card>

            {/* CONTACT DETAILS */}
            <Card
                title="Contact Details"
                extra={empId !== null ? renderEditButton('contact', {
                    email_id: employee?.email_id,
                    mobile_number: employee.mobile_number,
                    alternate_mobile_number: employee.alternate_mobile_number,
                    alternate_email_id: employee.alternate_email_id,
                    landline_extension: employee.landline_extension,
                    emergency_contact_person: employee.emergency_contact_person,
                    emergency_contact_number: employee.emergency_contact_number,
                    official_email_id: employee.official_email_id,
                    official_cug_number: employee.official_cug_number
                }) : null}
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                <Descriptions column={{ xs: 1, sm: 2 }} bordered
                    labelStyle={{ background: "#d0e5f8e4", color: '#0e487bff', fontWeight: 600 }}>
                    {/* <Descriptions.Item label="Primary Email">{employee?.user.email || "-"}</Descriptions.Item> */}
                    <Descriptions.Item label="Mobile Number">{employee.mobile_number || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Landline">{employee.landline_extension || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Emergency Contact Person">{employee.emergency_contact_person || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Emergency Contact Number">{employee.emergency_contact_number || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Personal Email ID">{employee?.email_id || "-"}</Descriptions.Item>
                </Descriptions>
            </Card>

            {/* JOB DETAILS */}
            <Card
                title="Job Details"
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                <Descriptions column={{ xs: 1, sm: 2 }} bordered
                    labelStyle={{ background: "#d0e5f8e4", color: '#0e487bff', fontWeight: 600 }}>
                    {/* <Descriptions.Item label="Employee Code">{employee.emp_code || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Designation">{employee.designation_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Department">{employee.department_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Organisation">{employee.organisation_name || "-"}</Descriptions.Item> */}
                    <Descriptions.Item label="Location">{employee.location || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Date of Joining">{formatDate(employee.date_of_joining)}</Descriptions.Item>
                    <Descriptions.Item label="Status">{employee.status || "-"}</Descriptions.Item>
                    {
                        (employee?.status === "Probation" || employee?.status === "PROBATION") && (
                            <Descriptions.Item label="Probation End Date">{employee.probation_end_date || "-"}</Descriptions.Item>
                        )
                    }
                    <Descriptions.Item label="Place Of Work">{employee.place_of_work_names || "-"}</Descriptions.Item>
                    {/* <Descriptions.Item label="Role ID">{employee.role || "-"}</Descriptions.Item> */}
                </Descriptions>
            </Card>

            {/* STATUTORY DETAILS */}
            <Card
                title="Statutory Details"
                extra={empId !== null ? renderEditButton('statutory', {
                    aadhaar_card_number: employee.aadhaar_card_number,
                    pan_card_number: employee.pan_card_number,
                    esic_ip_number: employee.esic_ip_number,
                    uan_number: employee.uan_number
                }) : null}
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                <Descriptions column={{ xs: 1, sm: 2 }} bordered
                    labelStyle={{ background: "#d0e5f8e4", color: '#0e487bff', fontWeight: 600 }}>
                    <Descriptions.Item label="Aadhaar Number">{employee.aadhaar_card_number || "-"}</Descriptions.Item>
                    <Descriptions.Item label="PAN Number">{employee.pan_card_number || "-"}</Descriptions.Item>
                    <Descriptions.Item label="ESIC IP Number">{employee.esic_ip_number || "-"}</Descriptions.Item>
                    <Descriptions.Item label="UAN Number">{employee.uan_number || "-"}</Descriptions.Item>
                </Descriptions>
            </Card>

            <Card
                title="Salary Details"
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                <Descriptions
                    column={{ xs: 1, sm: 2 }}
                    bordered
                    labelStyle={{
                        background: "#d0e5f8e4",
                        color: "#0e487bff",
                        fontWeight: 600
                    }}
                >
                    <Descriptions.Item label="Eligibility for PF">
                        {employee?.salary_details?.is_pf ? "Yes" : "No"}
                    </Descriptions.Item>


                    <Descriptions.Item label="Eligibility for ESI">
                        {employee?.salary_details?.is_esi ? "Yes" : "No"}
                    </Descriptions.Item>


                    <Descriptions.Item label="Basic salary">
                        {roundValue(employee?.salary_details?.basic_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="HRA">
                        {roundValue(employee?.salary_details?.hra_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="Conveyance Allowance">
                        {roundValue(employee?.salary_details?.conveyance_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="Other Allowance">
                        {roundValue(employee?.salary_details?.other_allowance_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="Gross">
                        {roundValue(employee?.salary_details?.gross_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="PF Employer">
                        {roundValue(employee?.salary_details?.pf_employer_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="ESI Employer">
                        {roundValue(employee?.salary_details?.esi_employer_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="PF Employee">
                        {roundValue(employee?.salary_details?.pf_employee_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="ESI Employee">
                        {roundValue(employee?.salary_details?.esi_employee_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="Professional Tax">
                        {roundValue(employee?.salary_details?.pt_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="Other Deductions">
                        {roundValue(employee?.salary_details?.deductions_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="CTC">
                        {roundValue(employee?.salary_details?.ctc_pm)}
                    </Descriptions.Item>


                    <Descriptions.Item label="Net Pay">
                        {roundValue(employee?.salary_details?.net_pay_pm)}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {/* BANK DETAILS */}
            <Card
                title="Bank Details"
                extra={empId !== null ? renderEditButton('bank', {
                    bank_name: employee.bank_name,
                    branch_name: employee.branch_name,
                    bank_account_number: employee.bank_account_number,
                    ifsc_code: employee.ifsc_code,
                    cub_account_number: employee.cub_account_number,
                    payment_mode: employee.payment_mode
                }) : null}
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                <Descriptions column={{ xs: 1, sm: 2 }} bordered
                    labelStyle={{ background: "#d0e5f8e4", color: '#0e487bff', fontWeight: 600 }}>
                    <Descriptions.Item label="Bank Name">{employee.bank_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Bank Branch">{employee.branch_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Account Number">{employee.bank_account_number || "-"}</Descriptions.Item>
                    <Descriptions.Item label="IFSC Code">{employee.ifsc_code || "-"}</Descriptions.Item>
                    <Descriptions.Item label="CUB Account Number">{employee.cub_account_number || "-"}</Descriptions.Item>
                </Descriptions>
            </Card>

            {/* ADDRESS DETAILS */}
            <Card
                title="Address Details"
                extra={empId !== null ? renderEditButton('address', {
                    address_for_communication: employee.address_for_communication,
                    city: employee.city,
                    state: employee.state,
                    country: employee.country,
                    pincode: employee.pincode,
                    permanent_address_for_communication: employee.permanent_address_for_communication,
                    permanent_city: employee.permanent_city,
                    permanent_state: employee.permanent_state,
                    permanent_country: employee.permanent_country,
                    permanent_pincode: employee.permanent_pincode,
                    permanent_area: employee.permanent_area,
                    area: employee.area
                }) : null}
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                <Title level={5}>Current Address</Title>
                <Descriptions column={{ xs: 1, sm: 2 }} bordered
                    labelStyle={{ background: "#d0e5f8e4", color: '#0e487bff', fontWeight: 600 }}>
                    <Descriptions.Item label="Address">{employee.address_for_communication || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Area">{employee.area || "-"}</Descriptions.Item>
                    <Descriptions.Item label="City">{employee.city || "-"}</Descriptions.Item>
                    <Descriptions.Item label="State">{employee.state || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Country">{employee.country || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Pincode">{employee.pincode || "-"}</Descriptions.Item>
                </Descriptions>
                <Divider />
                <Title level={5}>Permanent Address</Title>
                <Descriptions column={{ xs: 1, sm: 2 }} bordered
                    labelStyle={{ background: "#d0e5f8e4", color: '#0e487bff', fontWeight: 600 }}>
                    <Descriptions.Item label="Address">{employee.permanent_address_for_communication || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Area">{employee.permanent_area || "-"}</Descriptions.Item>
                    <Descriptions.Item label="City">{employee.permanent_city || "-"}</Descriptions.Item>
                    <Descriptions.Item label="State">{employee.permanent_state || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Country">{employee.permanent_country || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Pincode">{employee.permanent_pincode || "-"}</Descriptions.Item>
                </Descriptions>
            </Card>

            {/* EDUCATION HISTORY */}
            <Card
                title="Education History"
                extra={empId !== null ? renderEditButton('education', {
                    education_history: educationHistory.map(edu => ({
                        ...edu,
                        course_duration_from: edu.course_duration_from ? dayjs(edu.course_duration_from) : null,
                        course_duration_to: edu.course_duration_to ? dayjs(edu.course_duration_to) : null
                    }))
                }) : null}
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                {renderArrayTable(
                    educationHistory,
                    [
                        { title: 'Qualification', dataIndex: 'educational_qualification', key: 'qualification' },
                        { title: 'Institution', dataIndex: 'educational_institution_name', key: 'institution' },
                        { title: 'Degree/Diploma', dataIndex: 'degree_or_diploma', key: 'degree' },
                        { title: 'From', dataIndex: 'course_duration_from', key: 'from', render: formatDate },
                        { title: 'To', dataIndex: 'course_duration_to', key: 'to', render: formatDate },
                    ]
                )}
            </Card>

            {/* CAREER HISTORY */}
            <Card
                title="Career History"
                extra={empId !== null ? renderEditButton('career', {
                    career_history: careerHistory.map(career => ({
                        ...career,
                        work_duration_from: career.work_duration_from ? dayjs(career.work_duration_from) : null,
                        work_duration_to: career.work_duration_to ? dayjs(career.work_duration_to) : null
                    }))
                }) : null}
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                {renderArrayTable(
                    careerHistory,
                    [
                        { title: 'Organisation', dataIndex: 'organisation', key: 'organisation' },
                        { title: 'Designation', dataIndex: 'designation', key: 'designation' },
                        { title: 'Key Responsibilities', dataIndex: 'keyresponsibilities', key: 'responsibilities' },
                        { title: 'From', dataIndex: 'work_duration_from', key: 'from', render: formatDate },
                        { title: 'To', dataIndex: 'work_duration_to', key: 'to', render: formatDate },
                    ]
                )}
            </Card>

            {/* REFERENCE HISTORY */}
            <Card
                title="References"
                extra={empId !== null ? renderEditButton('reference', {
                    reference_history: referenceHistory
                }) : null}
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                {renderArrayTable(
                    referenceHistory,
                    [
                        { title: 'Name', dataIndex: 'referal_name', key: 'name' },
                        { title: 'Designation', dataIndex: 'referal_designation', key: 'designation' },
                        { title: 'Email', dataIndex: 'referal_email_id', key: 'email' },
                        { title: 'Mobile', dataIndex: 'referal_mobile_number', key: 'mobile' },
                    ]
                )}
            </Card>

            {/* SIBLING DETAILS */}
            <Card
                title="Sibling Details"
                extra={empId !== null ? renderEditButton('siblings', {
                    sibling_details: siblingDetails
                }) : null}
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                {renderArrayTable(
                    siblingDetails,
                    [
                        { title: 'Sibling Name', dataIndex: 'sibling_name', key: 'name' },
                        { title: 'Sibling DOB', dataIndex: 'sibling_dob', key: 'sibling_dob', render: formatDate },
                        { title: 'Gender', dataIndex: 'sibling_gender', key: 'occupation' },
                        { title: 'Profession', dataIndex: 'sibling_profession', key: 'contact' },
                    ]
                )}
            </Card>

            {/* CHILD DETAILS */}
            <Card
                title="Children Details"
                extra={empId !== null ? renderEditButton('children', {
                    child_details: childDetails.map(child => ({
                        ...child,
                        dob: child.dob ? dayjs(child.dob) : null
                    }))
                }) : null}
                style={{ marginBottom: 24, borderRadius: 12 }}
            >
                {renderArrayTable(
                    childDetails,
                    [
                        { title: 'Child Name', dataIndex: 'child_name', key: 'name' },
                        { title: 'Date of Birth', dataIndex: 'child_dob', key: 'dob', render: formatDate },
                        { title: 'Gender', dataIndex: 'child_gender', key: 'gender' },
                    ]
                )}
                <br />
                <br />
            </Card>


            {/* EDIT MODAL */}
            <Modal
                title={`Edit ${editSection.charAt(0).toUpperCase() + editSection.slice(1)} Details`}
                open={editModalVisible}
                onCancel={() => {
                    setEditModalVisible(false);
                    form.resetFields();
                }}
                footer={[
                    <Button key="cancel" icon={<CloseOutlined />} onClick={() => {
                        setEditModalVisible(false);
                        form.resetFields();
                    }}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={uploading}
                        onClick={handleEditSubmit}
                    >
                        Save Changes
                    </Button>
                ]}
                width={700}
                style={{ top: 20 }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 10 }}
                >
                    {getEditFormFields(editSection)}
                </Form>
            </Modal>
        </div>
    );
}